import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import Select from "react-select";
import { register } from "../../services/userService";

function Register() {
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirm_password: "",
    phone: "",
    company_name: "",
    company_phone: "",
    service_provided: [],
  });

  const [validationErrors, setValidationErrors] = useState({});

  const mutation = useMutation({
    mutationFn: (data) => register(data),
    onSuccess: (data) => {
      navigate("/verification-pending");
    },
    onError: (error) => {
      setError(
        error.response?.data?.message || error.message || "Something went wrong"
      );
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
  });

  const validateForm = () => {
    const errors = {};
    if (formData.password !== formData.confirm_password) {
      errors.confirm_password = "Passwords do not match";
    }
    if (!formData.service_provided?.length) {
      errors.service_provided = "Please select at least one service";
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    const formDataToSend = new FormData();
    Object.keys(formData).forEach((key) => {
      if (key === "service_provided") {
        formDataToSend.append(key, JSON.stringify(formData[key]));
      } else if (formData[key]) {
        formDataToSend.append(key, formData[key]);
      }
    });

    mutation.mutate(formDataToSend);
  };

  return (
    <div className="min-h-screen flex items-start bg-slate-50 font-sans text-slate-900 selection:bg-primary-100 selection:text-primary-900">
      {/* 1. Left Side - Sticky Brand Section */}
      {/* 1. Left Side - Sticky Brand Section */}
      <div className="relative hidden lg:flex lg:w-[42%] sticky top-0 h-screen shrink-0 flex-col justify-center overflow-hidden bg-gradient-to-br from-primary-700 via-primary-800 to-primary-950 px-12 py-16 xl:px-16">
        <div className="pointer-events-none absolute -left-24 top-1/4 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-20 -right-12 h-64 w-64 rounded-full bg-teal-400/15 blur-3xl" />

        <div className="relative z-10 max-w-md">
          <img
            src="https://www.exploreworld.com/_next/image?url=%2Fexploreworld-logo.png&w=256&q=75"
            alt="Explore World"
            className="mb-12 h-7 w-auto brightness-0 invert opacity-95"
          />
          <p className="text-sm font-medium text-teal-200/90">Join as a supplier</p>
          <h1 className="mt-3 text-3xl xl:text-[2.75rem] font-bold leading-tight tracking-tight text-white">
            Start selling on Explore World
          </h1>
          <p className="mt-5 text-base leading-relaxed text-white/65">
            List packages, activities, attractions and more — reach travelers worldwide.
          </p>
        </div>

        <p className="absolute bottom-8 left-12 xl:left-16 text-xs text-white/35">
          © {new Date().getFullYear()} Explore World
        </p>
      </div>

      {/* 2. Right Side - Registration Form (Scrollable) */}
      <div className="w-full lg:flex-1 flex justify-center p-6 lg:px-12 min-h-screen bg-slate-50">
        <div className="w-full max-w-xl py-8 lg:py-12">

          <div className="mb-8 mt-12 lg:mt-0">
            <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-2 tracking-tight">Create Supplier Account</h2>
            <p className="text-gray-500 text-sm leading-relaxed">Fill in the details below to register your business.</p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-xl flex items-start gap-3 animate-fade-in shadow-sm">
              <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center shrink-0 text-red-600 shadow-sm border border-red-50 mt-0.5">
                <i className="fi fi-rr-exclamation text-base"></i>
              </div>
              <div>
                <span className="font-bold text-red-900 block text-xs mb-0.5">Registration Failed</span>
                <span className="text-red-600 text-xs block leading-snug">{error}</span>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Section 1: Personal Info */}
            <section>
              <div className="flex items-center gap-3 mb-6">
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary-600 text-white font-bold text-xs">1</span>
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Personal Details</h3>
                <div className="h-px bg-gray-100 flex-1 ml-4"></div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label htmlFor="name" className="block text-xs font-bold text-gray-700 ml-1">Full Name</label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="block w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:border-primary-500 hover:border-primary-200 transition-all outline-none text-sm text-gray-900 placeholder:text-gray-400"
                    placeholder="e.g. John Doe"
                  />
                </div>
                <div className="space-y-1.5">
                  <label htmlFor="email" className="block text-xs font-bold text-gray-700 ml-1">Email Address</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="block w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:border-primary-500 hover:border-primary-200 transition-all outline-none text-sm text-gray-900 placeholder:text-gray-400"
                    placeholder="name@company.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div className="space-y-1.5">
                  <label htmlFor="phone" className="block text-xs font-bold text-gray-700 ml-1">Phone Number</label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    className="block w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:border-primary-500 hover:border-primary-200 transition-all outline-none text-sm text-gray-900 placeholder:text-gray-400"
                    placeholder="+91 9876543210"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div className="space-y-1.5">
                  <label htmlFor="password" className="block text-xs font-bold text-gray-700 ml-1">Password</label>
                  <div className="relative">
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      required
                      value={formData.password}
                      onChange={handleChange}
                      className="block w-full px-4 py-3 pr-10 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:border-primary-500 hover:border-primary-200 transition-all outline-none text-sm text-gray-900 placeholder:text-gray-400"
                      placeholder="Create a strong password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((prev) => !prev)}
                      className="absolute inset-y-0 right-0 flex items-center pr-3.5 text-gray-400 hover:text-gray-600"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      <i className={`fi ${showPassword ? "fi-rr-eye" : "fi-rr-eye-crossed"}`} />
                    </button>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label htmlFor="confirm_password" className="block text-xs font-bold text-gray-700 ml-1">Confirm Password</label>
                  <div className="relative">
                    <input
                      id="confirm_password"
                      name="confirm_password"
                      type={showConfirmPassword ? "text" : "password"}
                      required
                      value={formData.confirm_password}
                      onChange={handleChange}
                      className="block w-full px-4 py-3 pr-10 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:border-primary-500 hover:border-primary-200 transition-all outline-none text-sm text-gray-900 placeholder:text-gray-400"
                      placeholder="Repeat password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword((prev) => !prev)}
                      className="absolute inset-y-0 right-0 flex items-center pr-3.5 text-gray-400 hover:text-gray-600"
                      aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                    >
                      <i className={`fi ${showConfirmPassword ? "fi-rr-eye" : "fi-rr-eye-crossed"}`} />
                    </button>
                  </div>
                  {validationErrors.confirm_password && (
                    <p className="mt-1 text-xs text-red-600 font-medium pl-1">
                      {validationErrors.confirm_password}
                    </p>
                  )}
                </div>
              </div>
            </section>

            {/* Section 2: Company Info */}
            <section>
              <div className="flex items-center gap-3 mb-6">
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary-600 text-white font-bold text-xs">2</span>
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Business Information</h3>
                <div className="h-px bg-gray-100 flex-1 ml-4"></div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label htmlFor="company_name" className="block text-xs font-bold text-gray-700 ml-1">Company Name</label>
                  <input
                    id="company_name"
                    name="company_name"
                    type="text"
                    required
                    value={formData.company_name}
                    onChange={handleChange}
                    className="block w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:border-primary-500 hover:border-primary-200 transition-all outline-none text-sm text-gray-900 placeholder:text-gray-400"
                    placeholder="Legal Business Name"
                  />
                </div>
                <div className="space-y-1.5">
                  <label htmlFor="company_phone" className="block text-xs font-bold text-gray-700 ml-1">Company Phone</label>
                  <input
                    id="company_phone"
                    name="company_phone"
                    type="tel"
                    required
                    value={formData.company_phone}
                    onChange={handleChange}
                    className="block w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:border-primary-500 hover:border-primary-200 transition-all outline-none text-sm text-gray-900 placeholder:text-gray-400"
                    placeholder="Business Contact Number"
                  />
                </div>
                <div className="md:col-span-2 space-y-1.5">
                  <label htmlFor="service_provided" className="block text-xs font-bold text-gray-700 ml-1">
                    Services Provided <span className="text-red-500">*</span>
                  </label>
                  <Select
                    id="service_provided"
                    name="service_provided"
                    isMulti
                    value={formData.service_provided.map((service) => ({
                      value: service,
                      label: service.charAt(0).toUpperCase() + service.slice(1),
                    }))}
                    onChange={(selectedOptions) => {
                      handleChange({
                        target: {
                          name: "service_provided",
                          value: selectedOptions ? selectedOptions.map((option) => option.value) : [],
                        },
                      });
                    }}
                    options={[
                      { value: "package", label: "Package" },
                      { value: "activity", label: "Activity" },
                      { value: "attraction", label: "Attraction" },
                      { value: "rental", label: "Rental" },
                      { value: "event", label: "Event" },
                    ]}
                    classNames={{
                      control: (state) =>
                        `!bg-gray-50 !border-2 !rounded-xl !min-h-[46px] !px-1 ${
                          validationErrors.service_provided
                            ? "!border-red-300"
                            : state.isFocused
                              ? "!border-primary-500 !shadow-none"
                              : "!border-transparent"
                        } hover:!bg-gray-100`,
                      option: (state) => state.isSelected ? '!bg-primary-600' : state.isFocused ? '!bg-gray-100' : '!text-gray-700',
                      multiValue: () => '!bg-gray-200 !rounded-lg',
                      multiValueLabel: () => '!text-gray-800 !text-xs !font-bold',
                      multiValueRemove: () => '!text-gray-500 hover:!bg-gray-300 hover:!text-gray-800 !rounded-full',
                      menu: () => '!rounded-xl !shadow-xl !border-0 !mt-2 !overflow-hidden z-20',
                    }}
                    placeholder="Select services..."
                  />
                  {validationErrors.service_provided && (
                    <p className="mt-1 text-xs text-red-600 font-medium pl-1">
                      {validationErrors.service_provided}
                    </p>
                  )}
                </div>
              </div>
            </section>

            {/* Submit Area */}
            <div className="pt-8 border-t border-gray-100">
              <button
                type="submit"
                disabled={mutation.isPending}
                className="w-full flex justify-center py-3.5 bg-primary-600 text-white font-bold rounded-xl hover:bg-primary-700 transition-all shadow-lg shadow-primary-600/20 disabled:opacity-70 disabled:cursor-not-allowed text-sm"
              >
                {mutation.isPending ? (
                  <div className="flex items-center gap-2">
                    <span className="animate-spin h-4 w-4 border-2 border-white/30 border-t-white rounded-full"></span>
                    <span>Processing...</span>
                  </div>
                ) : (
                  "Create Supplier Account"
                )}
              </button>
              <div className="text-center mt-6">
                <p className="text-gray-500 text-xs font-medium">
                  Already have an account?{" "}
                  <a href="/login" className="font-bold text-primary-600 hover:text-primary-700 hover:underline transition-colors ml-1">
                    Sign in here
                  </a>
                </p>
              </div>
            </div>

          </form>
        </div>
      </div>
    </div>
  );
}

export default Register;
